/**
 * Overview tab content for the Collection Detail page.
 *
 * Read-only summary per `feature-spec.md` §2.6 — four cards stacked:
 *   1. Pipeline overview (7 lifecycle stages with counts)
 *   2. Progress Toward Target (fraction + progress bar + meta line)
 *   3. Key Metrics (overall IAA, adjudication rate, total finalised, this week)
 *   4. Annotator Activity (list of users)
 *
 * All counts come from `useCollectionOverview` (A4 endpoint). Pre-B1
 * the wire returns zeros / nulls; the tab renders coherently anyway.
 */

import React from 'react';

import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Progress } from '../../../ui/progress';
import { useCollectionOverview } from './hooks/useDatasets';
import type { Collection, PipelineCounts } from './types';
import EmptyTabState from './components/EmptyTabState';

interface Props {
  collection: Collection;
}

const PIPELINE_STAGES: ReadonlyArray<readonly [keyof PipelineCounts, string]> = [
  ['pending', 'Pending'],
  ['inProgress', 'In Progress'],
  ['annotatedFirst', 'Annotated (1st)'],
  ['disputed', 'Disputed'],
  ['agreed', 'Agreed'],
  ['finalised', 'Finalised'],
];

const formatPercent = (value: number | null): string =>
  value === null ? '—' : `${Math.round(value * 100)}%`;

const OverviewTab: React.FC<Props> = ({ collection }) => {
  const { data, isLoading, isError, refetch } = useCollectionOverview(
    collection.id,
    { enabled: collection.status === 'active' },
  );

  if (collection.status !== 'active') {
    return <EmptyTabState tabName="overview" />;
  }

  if (isLoading) {
    return (
      <div className="space-y-3" data-testid="overview-loading">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="h-32 animate-pulse rounded-lg border border-border/70 bg-card"
          />
        ))}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex h-[40vh] flex-col items-center justify-center text-center">
        <p className="mb-4 text-muted-foreground">Failed to load overview.</p>
        <Button variant="link" size="sm" onClick={() => refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  const progressPct = Math.round(data.progressToTarget * 100);

  return (
    <div className="space-y-4" data-testid="overview-tab">
      {/* 1. Pipeline overview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Pipeline overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div
            className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-6"
            data-testid="overview-pipeline"
          >
            {PIPELINE_STAGES.map(([key, label]) => (
              <div
                key={key}
                className="rounded-md border border-border bg-card p-3 text-center"
                data-testid={`pipeline-${key}`}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {label}
                </div>
                <div className="mt-1 text-xl font-bold tabular-nums text-foreground">
                  {data.pipeline[key].toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 2. Progress Toward Target */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Progress Toward Target</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3" data-testid="overview-progress">
            <div className="text-2xl font-bold tabular-nums text-foreground">
              {data.keyMetrics.totalFinalised.toLocaleString()}{' '}
              <span className="text-muted-foreground">/{' '}{data.target.toLocaleString()}</span>
            </div>
            <Progress value={progressPct} max={100} />
            <p
              className="text-[12px] text-muted-foreground"
              data-testid="overview-progress-meta"
            >
              {progressPct}% complete · {data.keyMetrics.thisWeek.toLocaleString()} annotations this week
            </p>
          </div>
        </CardContent>
      </Card>

      {/* 3. Key Metrics */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Key Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="overview-key-metrics">
            <MetricTile
              label="Overall IAA"
              value={formatPercent(data.keyMetrics.overallIaa)}
              testid="metric-iaa"
            />
            <MetricTile
              label="Adjudication Rate"
              value={formatPercent(data.keyMetrics.adjudicationRate)}
              testid="metric-adj-rate"
            />
            <MetricTile
              label="Total Finalised"
              value={data.keyMetrics.totalFinalised.toLocaleString()}
              testid="metric-finalised"
            />
            <MetricTile
              label="This Week"
              value={data.keyMetrics.thisWeek.toLocaleString()}
              testid="metric-this-week"
            />
          </div>
        </CardContent>
      </Card>

      {/* 4. Annotator Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-semibold">Annotator Activity</CardTitle>
        </CardHeader>
        <CardContent>
          {data.annotatorActivity.length === 0 ? (
            <p
              className="py-4 text-center text-[13px] text-muted-foreground"
              data-testid="overview-annotators-empty"
            >
              No annotator activity yet.
            </p>
          ) : (
            <ul className="divide-y divide-border" data-testid="overview-annotators">
              {data.annotatorActivity.map((a) => (
                <li
                  key={a.userId}
                  className="flex items-center justify-between py-2 text-[13px]"
                  data-testid={`annotator-${a.userId}`}
                >
                  <div>
                    <div className="font-semibold text-foreground">{a.name}</div>
                    <div className="text-[11px] text-muted-foreground">{a.role}</div>
                  </div>
                  <div className="text-[11px] tabular-nums text-muted-foreground">
                    {a.annotatedCount.toLocaleString()} annotated · {a.finalisedCount.toLocaleString()} finalised
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const MetricTile: React.FC<{ label: string; value: string; testid: string }> = ({
  label,
  value,
  testid,
}) => (
  <div className="rounded-md border border-border bg-card p-3 text-center" data-testid={testid}>
    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
      {label}
    </div>
    <div className="mt-1 text-xl font-bold tabular-nums text-foreground">{value}</div>
  </div>
);

export default OverviewTab;
